/** Profile & features — the setup page's editor for the onboarding answers.
 *
 *  The same questions the create wizard asks (shared StepProfile component),
 *  now server-backed: GET/PATCH /tour_operators/{id}/onboarding_profile/.
 *  These answers drive which preflight items apply, and applicability is
 *  computed live — so a save invalidates the items query and reports the
 *  applicability diff. Languages are deliberately absent here (config.i18n is
 *  the I18N_CONFIG item's job on the Parameters step).
 *
 *  The profile itself is never edited here. On a sandbox it is one of the sandbox profiles, set by
 *  sandbox provisioning (metaplan F6, M11) — shown read-only above the answers, which stay editable:
 *  "has a mobile app" set to yes is the sandbox's own app (its mobile items, metaplan S8).
 *
 *  has_app is the Tour_Operator.has_app column. A licensee or a sandbox of a live operator can
 *  inherit it (`has_app_inherits_from`): the answer is then inherit / yes / no, sent as null /
 *  true / false (plan 2026-10-29-tour-operator-has-app-field).
 */
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import {
  onboardingProfile,
  type OnboardingProfile,
  type OnboardingProfilePatch,
  type PreflightItem,
} from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';

import { StepProfile } from '../../wizard/StepProfile';
import { WIZARD_DEFAULTS, type WizardData } from '../../wizard/types';

const PROFILE_KEYS = [
  'restech',
  'channels',
  'cadence',
  'guides_split_chat',
  'vehicle_tracking',
  'own_corporate_email',
  'app_store_account',
  'has_existing_apps',
  'tag_category_set',
  'ag_template_group',
  'reviews_enabled',
  'reviews_alert_email',
] as const;

type ProfileKey = (typeof PROFILE_KEYS)[number];

const SANDBOX_PROFILE_LABEL = {
  sandbox: 'Sandbox',
  partner_sandbox: 'Partner sandbox',
} as const;

function isSandboxProfile(p: OnboardingProfile['profile']): p is keyof typeof SANDBOX_PROFILE_LABEL {
  return p === 'sandbox' || p === 'partner_sandbox';
}

function seed(profile: OnboardingProfile): WizardData {
  const data = { ...WIZARD_DEFAULTS };
  for (const key of PROFILE_KEYS) {
    // The profile keys are a (typed) subset of WizardData — same names, same shapes.
    (data as Record<string, unknown>)[key] = profile[key];
  }
  data.has_app = profile.has_app; // the effective answer (an inherited one included)
  return data;
}

interface Props {
  topId: number;
  profile: OnboardingProfile;
  items: PreflightItem[];
  canManage: boolean;
}

export function ProfileStep({ topId, profile, items, canManage }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [data, setData] = useState<WizardData>(() => seed(profile));
  // A licensee / sandbox: its own answer, null = inherited (data.has_app shows the effective one).
  const upstream = profile.has_app_inherits_from ?? null;
  const [appOwn, setAppOwn] = useState<boolean | null>(profile.has_app_own ?? null);
  const [diff, setDiff] = useState<{ added: string[]; removed: string[] } | null>(null);

  const appDirty = upstream ? appOwn !== (profile.has_app_own ?? null) : data.has_app !== profile.has_app;
  const dirtyKeys = useMemo(
    () => [
      ...PROFILE_KEYS.filter(
        (key) => JSON.stringify(data[key as keyof WizardData]) !== JSON.stringify(profile[key]),
      ),
      ...(appDirty ? (['has_app'] as const) : []),
    ],
    [data, profile, appDirty],
  );

  const save = useMutation({
    mutationFn: () => {
      const body: OnboardingProfilePatch = {};
      for (const key of dirtyKeys) {
        if (key === 'has_app') {
          body.has_app = upstream ? appOwn : data.has_app;
        } else {
          (body as Record<string, unknown>)[key] = data[key as keyof WizardData];
        }
      }
      return onboardingProfile.patch(topId, body);
    },
    onSuccess: async (updated) => {
      const before = new Set(items.filter((i) => i.applicable).map((i) => i.action));
      setData(seed(updated));
      setAppOwn(updated.has_app_own ?? null);
      await qc.invalidateQueries({ queryKey: ['preflight', 'items', topId] });
      await qc.invalidateQueries({ queryKey: ['preflight', 'onboarding-profile', topId] });
      const after = qc.getQueryData<PreflightItem[]>(['preflight', 'items', topId]);
      if (after) {
        const now = new Set(after.filter((i) => i.applicable).map((i) => i.action));
        setDiff({
          added: [...now].filter((a) => !before.has(a)),
          removed: [...before].filter((a) => !now.has(a)),
        });
      }
      snackbar.show('Profile saved — applicability recomputed.', 'success');
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  return (
    <Stack spacing={2}>
      {isSandboxProfile(profile.profile) && (
        <Box data-sandbox-profile>
          <Typography variant="caption" color="text.secondary" component="div">
            Onboarding profile
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {SANDBOX_PROFILE_LABEL[profile.profile]}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set by sandbox provisioning; it does not change here. Only the sandbox’s items apply: no billing,
            branded e-mail or go-live. Give the sandbox its own mobile app (“Yes”) for its TestFlight and internal
            testing builds.
          </Typography>
        </Box>
      )}
      <Alert severity="info">
        Changing these answers recomputes which setup items apply — the rail and the checklist
        update on save.
      </Alert>
      {diff && (diff.added.length > 0 || diff.removed.length > 0) && (
        <Alert severity="success" onClose={() => setDiff(null)}>
          Applicability recomputed
          {diff.added.length > 0 && ` — now applicable: ${diff.added.join(', ')}`}
          {diff.removed.length > 0 && ` — no longer applicable: ${diff.removed.join(', ')}`}.
        </Alert>
      )}
      <StepProfile
        data={data}
        onChange={(partial) => setData((prev) => ({ ...prev, ...partial }))}
        hideLanguages
        deriveTagSetOnMount={false}
        appChoice={
          upstream
            ? {
                from: upstream,
                own: appOwn,
                sandbox: isSandboxProfile(profile.profile),
                onChange: (own) => {
                  setAppOwn(own);
                  setData((prev) => ({ ...prev, has_app: own ?? upstream.has_app }));
                },
              }
            : undefined
        }
      />
      {canManage && (
        <Box>
          <Button
            variant="contained"
            disabled={dirtyKeys.length === 0 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : `Save profile${dirtyKeys.length ? ` (${dirtyKeys.length})` : ''}`}
          </Button>
          {dirtyKeys.length > 0 && (
            <Button
              sx={{ ml: 1 }}
              color="inherit"
              disabled={save.isPending}
              onClick={() => {
                setData(seed(profile));
                setAppOwn(profile.has_app_own ?? null);
              }}
            >
              Discard
            </Button>
          )}
        </Box>
      )}
    </Stack>
  );
}

export type { ProfileKey };
